import React, { useCallback, useEffect, useMemo } from "react";
import { SyncOutlined, ScheduleOutlined } from "@ant-design/icons";
import { Radio } from "antd";
import classNames from "classnames";

import {
  Checkbox,
  Input,
  GooglePlacesAutoComplete,
  CustomCheckbox,
  InputTextArea,
} from "../../..";

import { LodashHelper } from "../../../../utils";
import {
  INVALID_ADDRESS_MESSAGE,
  UBER_SERVICE_UNAVAILABLE_MESSAGE,
} from "../../../../constants/constants";
import { Snackbar } from "../../../common/Snackbar/Snackbar";
import { isAddressInvalid } from "../../../../utils/helpers";

import { useStepThreeHandlers } from "./useStepThreeHandlers";
import { RecurringSection } from "./RecurringSection";
import { useDateTimeHandlers } from "./useDateTimeHandlers";
import { ReturnDateTimeSection } from "./ReturnDateTimeSection";
import { PickUpDateTimeSection } from "./PickUpDateTimeSection";
import { useCheckForValidWindowOfTime } from "../../../../hooks/useCheckForValidWindowOfTime";
import { ThirdStepTooltip } from "./ThirdStepTooltip";

import GoogleDistanceMatrix from "../../../common/GoogleMaps/DistanceMatrix";
import DateHelperInstance from "../../../../utils/DateHelper";

import styles from "./index.module.css";
import TrackSectionStickiness from "../../../analytics/TrackSectionStickiness ";
import { isDriverRemainOnSiteSchedulingWindow } from "../../../../utils/RideHelper";

export const StepThreeFormFields = ({
  values,
  touched,
  errors,
  validateForm,
  setFieldTouched,
  setFieldValue,
  defaultValues,
  disabledValues,
  minDelayToStartRide,
  hasMatchedTC,
  defaultHomeAddress,
  maxPickUpWindowOfTime,
  vehicleType,
  windowOfTimeIsValid,
  minimumRoundTrip,
  airportBufferTime,
  quickSchdule = false,
  isUberRide = false,
  uberHealthRouteAvailable = true,
  uberHealthRouteCheckPending = false,
}) => {

  const {
    pickUpAddress,
    destinationAddress,
    handleSelectAddress,
    handleChangeAddress,
    handleAddressBookSelectAddress,
    handleChangePublicPlace,
    handleUseDefaultHomeAddress,
    handleSecurityGateChange,
  } = useStepThreeHandlers({
    defaultHomeAddress,
    validateForm,
    setFieldValue,
  });

  const {
    handlePickUpDateTime,
    handleReturnTime,
    handleStartPointTime,
    handleReturnStartPoint,
    handleRoundTripCheckbox,
    disabledDatesForPickUpDate,
    disabledDatesForAppointment,
    disabledDatesForReturnTime,
    disabledDatesForStartPointOfRecurring,
    disabledDatesForReturnTimeForRoundTripRecurring,
    disabledDateTime,
    handleChangeTZ,
    pickUpDate,
    returnTimeDate,
    startPointOfRecurring,
    returnStartPoint,
    setStartPointOfRecurring,
    setReturnStartPoint,
    handleReccuringCheckbox,
    handleUseWindowOfTime,
    disabledDateForHigherLimit,
    disabledTimeForHigherLimit,
    handleChangePickUpDateToLowerLimit,
    pickUpDateLowerLimit,
    returnTimeLowerLimit,
    handleReturnTimeLowerLimit,
    disabledDateForReturnHigherLimit,
    disabledDateTimeForReturnHigherLimit,
    handleChangeappointmentDate,
    appointmentDate,
    handleAppointmentDateCheckBox,
  } = useDateTimeHandlers({
    validateForm,
    setFieldTouched,
    defaultValues,
    minDelayToStartRide,
    maxPickUpWindowOfTime,
    minimumRoundTrip,
    isUberRide,
  });



  useCheckForValidWindowOfTime({
    defaultValues,
    currentValues: values,
    vehicleType,
  });

  const showOnSiteDriverWait = useMemo(
    () =>
      Boolean(
        values?.isRoundtrip &&
          !values?.isRecurring &&
          !isUberRide &&
          isDriverRemainOnSiteSchedulingWindow(values?.pickUpDate, values?.returnTime)
      ),
    [
      values?.isRoundtrip,
      values?.isRecurring,
      values?.pickUpDate,
      values?.returnTime,
      isUberRide,
    ]
  );

  useEffect(() => {
    if (!showOnSiteDriverWait && values?.driverRemainOnSite) {
      setFieldValue("driverRemainOnSite", false);
    }
  }, [showOnSiteDriverWait, values?.driverRemainOnSite, setFieldValue]);

  useEffect(() => {
    if (isUberRide && values?.isRoundtrip && values?.isPickUpDateFlexible) {
      setFieldValue("isPickUpDateFlexible", false);
      setFieldValue("pickUpDateLowerLimit", undefined);
    }
  }, [isUberRide, values?.isRoundtrip, values?.isPickUpDateFlexible, setFieldValue]);

  useEffect(() => {
    validateForm();
    return;
  }, [pickUpAddress, destinationAddress, validateForm]);

  const handleChildValueChange = useCallback(
    async (fieldName, updatedValue) => {
      await setFieldValue(fieldName, updatedValue);
    },
    [setFieldValue]
  );


  const updatedPickupDateTime = useMemo(() => {
    if (values?.flightInfo && pickUpDate && airportBufferTime) {
      let addedMinutesDate = DateHelperInstance.addMinutesInDateWithFormat(pickUpDate, airportBufferTime);
      return addedMinutesDate;
    }

    return null;
  }, [pickUpDate, values?.flightInfo, airportBufferTime]);


  return (
    <div className={styles.formContent}>
      <TrackSectionStickiness sectionName="Create Ride Step 3" />
      {!quickSchdule && <h3 className={styles.subHeadings}>Location Information:</h3>}

      {/* <Field
        name="pickupAddressDetails.formattedAddress"
        value={pickUpAddress}
       
        />   */}




      <label className={styles.addressLabel}>
        <span className={styles.required}>Pick Up Address</span>
        <GooglePlacesAutoComplete
          name="pickupAddressDetails.formattedAddress"
          handleSelectAddress={handleSelectAddress("pickupAddressDetails")}
          handleAddressBookSelectAddress={handleAddressBookSelectAddress("pickupAddressDetails")}
          handleChangeAddress={handleChangeAddress("pickupAddressDetails")}
          placeholder="Pick Up Address"
          value={pickUpAddress}
          myclassname={styles.googleAutoComplete}
          disabled={disabledValues?.pickupAddressDetails?.formattedAddress}
          whenFieldDisabledShowTooltip={Boolean(
            disabledValues?.pickupAddressDetails?.formattedAddress
          )}
          showFavoriteIcon={values?.pickupAddressDetails?.formattedAddress}
          currentAddress={values?.pickupAddressDetails?.formattedAddress}
          currentAddressObj={values?.pickupAddressDetails}
        // insertFavoriteAddress={insertFavoriteAddress}
        // handleRemoveAddress={handleRemoveAddress}

        />

        {touched &&
          touched["pickupAddressDetails.formattedAddress"] &&
          isAddressInvalid(errors?.pickupAddressDetails) && (
            <div className={styles.errorMessage} role="alert">
              {INVALID_ADDRESS_MESSAGE}
            </div>
          )}
        {values?.destinationAddress?.formattedAddress !== '' && values?.pickupAddressDetails?.formattedAddress !== '' ? (
          <GoogleDistanceMatrix
            destination={values.destinationAddress.formattedAddress}
            origin={values.pickupAddressDetails.formattedAddress}
            handleChildValueChange={handleChildValueChange}
          />
        ) : (
          ""
        )}
        {defaultHomeAddress && (
          <label className={styles.useDefaultAddress}>
            <CustomCheckbox
              name="useDefaultPickUpAddress"
              onChange={handleUseDefaultHomeAddress("pickupAddressDetails")}
              checked={LodashHelper.isEqual(
                values?.pickupAddressDetails,
                defaultHomeAddress
              )}
              disabled={disabledValues?.pickupAddressDetails?.formattedAddress}
              whenFieldDisabledShowTooltip={Boolean(
                disabledValues?.pickupAddressDetails?.formattedAddress
              )}
            />
            <span className={styles.checkboxLabelText}>
              Apply Home Address from Rider's Profile
            </span>
          </label>
        )}
      </label>
      {!quickSchdule && !isUberRide &&
        <label className={styles.facilityName}>
          Facility Name (Information Provided by Google)
          <Input
            name="pickupAddressDetails.facilityName"
            type="text"
            placeholder="Facility Name"
            disabled
            whenFieldDisabledShowTooltip={Boolean(
              disabledValues?.pickupAddressDetails?.formattedAddress
            )}
          />
        </label>
      }
      {!hasMatchedTC && values?.vehicleType !== 'uberAmbulatoryRide' && (
        <div className={styles.snackbarField}>
          <Snackbar
            type="error"
            title="No Suitable Vehicles"
            content="There are no suitable vehicles in this area. Please, change the Pick up/Destination addresses or the vehicle type in the previous step"
          />
        </div>
      )}
      {/* Pickup/destination Uber availability check loader — hidden per product request */}
      {/* {isUberRide && uberHealthRouteCheckPending && (
        <div className={styles.snackbarField}>
          <div className={styles.availabilityChecking} role="status">
            <LoadingOutlined spin style={{ marginRight: 8 }} />
            Availability checking...
          </div>
        </div>
      )} */}
      {isUberRide &&
        !uberHealthRouteAvailable &&
        !uberHealthRouteCheckPending && (
          <div className={styles.snackbarField}>
            <Snackbar
              type="error"
              title="Uber Unavailable"
              content={UBER_SERVICE_UNAVAILABLE_MESSAGE}
            />
          </div>
        )}


      {!quickSchdule && !isUberRide && (
        <>
          <label className={styles.facilityName}>
            If the rider is arriving at an airport, is there relevant flight
            information?
            <Input
              name="flightInfo"
              type="text"
              disabled={disabledValues?.flightInfo}
              whenFieldDisabledShowTooltip={Boolean(disabledValues?.flightInfo)}
              placeholder="Flight Number, ex DL1234 or Delta Flight 1234"
            />
          </label>

          {!values?.pickupAddressDetails?.isPublicPlace && (
            <div className={styles.address}>
              <label className={styles.building}>
                <span>Building/Suite Number</span>
                <Input
                  name="pickupAddressDetails.buildingNumber"
                  type="text"
                  placeholder="Building/Suite #"
                  disabled={disabledValues?.pickupAddressDetails?.buildingNumber}
                  whenFieldDisabledShowTooltip={Boolean(
                    disabledValues?.pickupAddressDetails?.buildingNumber
                  )}
                />
              </label>
              <label className={styles.apartment}>
                Apartment Number
                <Input
                  name="pickupAddressDetails.apartmentNum"
                  type="text"
                  placeholder="Apartment #"
                  disabled={disabledValues?.pickupAddressDetails?.apartmentNum}
                  whenFieldDisabledShowTooltip={Boolean(
                    disabledValues?.pickupAddressDetails?.apartmentNum
                  )}
                />
              </label>
            </div>
          )}

          <label className={styles.apartmentCheckbox}>
            <Checkbox
              name="pickupAddressDetails.isPublicPlace"
              checked={values?.pickupAddressDetails?.isPublicPlace}
              onChange={handleChangePublicPlace({
                values,
                name: "pickupAddressDetails",
              })}
              disabled={disabledValues?.pickupAddressDetails?.isPublicPlace}
              whenFieldDisabledShowTooltip={Boolean(
                disabledValues?.pickupAddressDetails?.isPublicPlace
              )}
            />
            <span className={styles.checkboxLabelText}>This is a public space</span>
          </label>

          <label className={styles.securityCheckbox}>
            <Checkbox
              name="pickUpSecurityGateDetails.hasSecurityGate"
              checked={values?.pickUpSecurityGateDetails?.hasSecurityGate}
              onChange={handleSecurityGateChange({
                values,
                name: "pickUpSecurityGateDetails",
              })}
              disabled={Boolean(disabledValues?.pickUpSecurityGateDetails?.hasSecurityGate)}
            // whenFieldDisabledShowTooltip={Boolean(
            //   disabledValues?.pickUpSecurityGateDetails?.hasSecurityGate
            // )}
            />
            <span className={styles.checkboxLabelText}>
              There is a security gate
            </span>
          </label>

          {values?.pickUpSecurityGateDetails?.hasSecurityGate && (
            <div className={styles.address}>
              <label className={styles.securityInstruction}>
                Gate Entry Instruction
                <Input
                  name="pickUpSecurityGateDetails.securityGateInstruction"
                  type="text"
                  placeholder=" Gate Entry Instruction"
                  disabled={Boolean(
                    disabledValues?.pickUpSecurityGateDetails?.securityGateInstruction
                  )}
                // whenFieldDisabledShowTooltip={Boolean(
                //   disabledValues?.pickUpSecurityGateDetails?.securityGateInstruction
                // )}
                />
              </label>
            </div>
          )}

          {!isUberRide && (
            <>
              <div className={styles.locationDescription}>
                <label className={styles.buildingAdditionalInfo}>
                  Pick Up Location Description
                  <Input
                    name="pickupAddressDetails.locationDescription"
                    type="text"
                    placeholder="Location Description"
                    disabled={disabledValues?.pickupAddressDetails?.locationDescription}
                    whenFieldDisabledShowTooltip={Boolean(
                      disabledValues?.pickupAddressDetails?.locationDescription
                    )}
                  />
                </label>

                <label className={styles.buildingAdditionalInfo}>
                  Rider Description
                  <Input
                    name="riderDescription"
                    type="text"
                    placeholder="Rider Description"
                    disabled={disabledValues?.pickupAddressDetails?.locationDescription}
                    whenFieldDisabledShowTooltip={Boolean(
                      disabledValues?.riderInfo?.riderDescription
                    )}
                  />
                </label>
              </div>
            </>
          )}
        </>
      )}
      {!isUberRide && (
        <div className={styles.otherInformation}>
          <label>
            <span>Other information /Notes:</span>
            <InputTextArea
              name="rideNotes"
              placeholder="Other information (if any)"
              type="text"
              autosize={{ minRows: 1, maxRows: 5 }}
              disabled={disabledValues?.rideNotes}
            />
          </label>
        </div>
      )}
      <div className={styles.destinationAddress}>
        <span className={styles.required}>Destination Address</span>
        <GooglePlacesAutoComplete
          name="destinationAddress.formattedAddress"
          handleSelectAddress={handleSelectAddress("destinationAddress")}
          handleChangeAddress={handleChangeAddress("destinationAddress")}
          handleAddressBookSelectAddress={handleAddressBookSelectAddress("destinationAddress")}
          placeholder="Destination Address"
          value={destinationAddress}
          myclassname={styles.googleAutoComplete}
          disabled={disabledValues?.destinationAddress?.formattedAddress}
          whenFieldDisabledShowTooltip={Boolean(
            disabledValues?.destinationAddress?.formattedAddress
          )}
          showFavoriteIcon={values?.destinationAddress?.formattedAddress}
          currentAddress={values?.destinationAddress?.formattedAddress}
          currentAddressObj={values?.destinationAddress}
        //insertFavoriteAddress={insertFavoriteAddress}
        // handleRemoveAddress={handleRemoveAddress}

        />

        <div className={styles.errorMessage} role="alert">
          {LodashHelper.isString(errors.destinationAddress)
            ? errors.destinationAddress
            : touched &&
            touched["destinationAddress.formattedAddress"] &&
            errors?.destinationAddress &&
            isAddressInvalid(errors?.destinationAddress) &&
            INVALID_ADDRESS_MESSAGE}
        </div>
        {defaultHomeAddress && (
          <label className={styles.useDefaultAddress}>
            <CustomCheckbox
              name="useDefaultDestinationAddress"
              onChange={handleUseDefaultHomeAddress("destinationAddress")}
              checked={LodashHelper.isEqual(
                values?.destinationAddress,
                defaultHomeAddress
              )}
              disabled={disabledValues?.destinationAddress?.formattedAddress}
              whenFieldDisabledShowTooltip={Boolean(
                disabledValues?.destinationAddress?.formattedAddress
              )}
            />
            <span className={styles.checkboxLabelText}>
              Apply Home Address from Rider's Profile
            </span>
          </label>
        )}

        {!quickSchdule && !isUberRide && (
          <>
            <label className={styles.facilityName}>
              Facility Name (Information Provided by Google)
              <Input
                name="destinationAddress.facilityName"
                type="text"
                placeholder="Facility Name"
                disabled
                whenFieldDisabledShowTooltip={Boolean(
                  disabledValues?.destinationAddress?.formattedAddress
                )}
              />
            </label>
            {!values?.destinationAddress?.isPublicPlace && (
              <div className={styles.address}>
                <label className={styles.building}>
                  <span>Building/Suite Number</span>
                  <Input
                    name="destinationAddress.buildingNumber"
                    type="text"
                    placeholder="Building/Suite #"
                    disabled={disabledValues?.destinationAddress?.buildingNumber}
                    whenFieldDisabledShowTooltip={Boolean(
                      disabledValues?.destinationAddress?.buildingNumber
                    )}
                  />
                </label>
                <label className={styles.apartment}>
                  Apartment Number
                  <Input
                    name="destinationAddress.apartmentNum"
                    type="text"
                    placeholder="Apartment #"
                    disabled={disabledValues?.destinationAddress?.apartmentNum}
                    whenFieldDisabledShowTooltip={Boolean(
                      disabledValues?.destinationAddress?.apartmentNum
                    )}
                  />
                </label>
              </div>
            )}
            <label className={styles.apartmentCheckbox}>
              <Checkbox
                name="destinationAddress.isPublicPlace"
                checked={values?.destinationAddress?.isPublicPlace}
                onChange={handleChangePublicPlace({
                  values,
                  name: "destinationAddress",
                })}
                disabled={disabledValues?.destinationAddress?.isPublicPlace}
                whenFieldDisabledShowTooltip={Boolean(
                  disabledValues?.destinationAddress?.isPublicPlace
                )}
              />
              <span className={styles.checkboxLabelText}>
                This is a public space
              </span>
            </label>
          </>
        )}
      </div>

      {!quickSchdule && !isUberRide && (
        <>
          <label className={styles.securityCheckbox}>
            <Checkbox
              name="destinationSecurityGateDetail.hasSecurityGate"
              checked={values?.destinationSecurityGateDetail?.hasSecurityGate}
              onChange={handleSecurityGateChange({
                values,
                name: "destinationSecurityGateDetail",
              })}
            // disabled={
            //   disabledValues?.destinationSecurityGateDetail?.hasSecurityGate
            // }
            // whenFieldDisabledShowTooltip={Boolean(
            //   disabledValues?.destinationSecurityGateDetail?.hasSecurityGate
            // )}
            />
            <span className={styles.checkboxLabelText}>
              There is a security gate
            </span>
          </label>
          {values?.destinationSecurityGateDetail?.hasSecurityGate && (
            <div className={styles.address}>
              <label className={styles.securityInstruction}>
                Gate Entry Instruction
                <Input
                  name="destinationSecurityGateDetail.securityGateInstruction"
                  type="text"
                  placeholder=" Gate Entry Instruction"
                  disabled={
                    disabledValues?.destinationSecurityGateDetail
                      ?.securityGateInstruction
                  }
                  whenFieldDisabledShowTooltip={Boolean(
                    disabledValues?.destinationSecurityGateDetail
                      ?.securityGateInstruction
                  )}
                />
              </label>
            </div>
          )}
        </>
      )}

      <PickUpDateTimeSection
        handleUseWindowOfTime={handleUseWindowOfTime}
        handlePickUpDateTime={handlePickUpDateTime}
        handleChangeTZ={handleChangeTZ}
        pickUpDate={pickUpDate}
        disabledDatesForPickUpDate={disabledDatesForPickUpDate}
        disabledDatesForAppointment={disabledDatesForAppointment}
        disabledDateTime={disabledDateTime}
        setFieldValue={setFieldValue}
        values={values}
        windowOfTimeIsValid={windowOfTimeIsValid}
        disabledValues={disabledValues}
        minDelayToStartRide={minDelayToStartRide}
        maxPickUpWindowOfTime={maxPickUpWindowOfTime}
        disabledDateForHigherLimit={disabledDateForHigherLimit}
        disabledTimeForHigherLimit={disabledTimeForHigherLimit}
        handleChangePickUpDateToLowerLimit={handleChangePickUpDateToLowerLimit}
        pickUpDateLowerLimit={pickUpDateLowerLimit}
        handleChangeappointmentDate={handleChangeappointmentDate}
        appointmentDate={appointmentDate}
        handleAppointmentDateCheckBox={handleAppointmentDateCheckBox}
        showTitle={!quickSchdule}
        isUberRide={isUberRide}

      />
      {values?.flightInfo && updatedPickupDateTime ? (
        <p style={{ color: 'red' }}>
          {`Note: Your ride is scheduled for ${updatedPickupDateTime} allowing time for baggage collection and airport formalities.`}
        </p>
      ) : null}
      {(
        <>
          <label
            className={classNames({
              [styles.roundtrip]: true,
              [styles.roundtripChecked]: values?.isRoundtrip === true,
            })}
          >
            <Checkbox
              name="isRoundtrip"
              checked={values?.isRoundtrip === true}
              onChange={handleRoundTripCheckbox}
              disabled={disabledValues?.isRoundTrip}
              whenFieldDisabledShowTooltip={Boolean(disabledValues?.isRoundTrip)}
            />
            <span className={styles.checkboxLabelText}>Roundtrip ride</span>
            <SyncOutlined className={styles.icon} />
            <ThirdStepTooltip
              range={minimumRoundTrip}
              useFlexibleHoursMessage={false}
            />
          </label>
          <br />
        </>
      )}
      <ReturnDateTimeSection
        pickUpDate={pickUpDate}
        appointmentDate={appointmentDate}
        returnTimeDate={returnTimeDate}
        maxPickUpWindowOfTime={maxPickUpWindowOfTime}
        handleReturnTime={handleReturnTime}
        disabledDatesForReturnTime={disabledDatesForReturnTime}
        disabledDateTime={disabledDateTime}
        handleUseWindowOfTime={handleUseWindowOfTime}
        handleChangeTZ={handleChangeTZ}
        values={values}
        minimumRoundTrip={minimumRoundTrip}
        disabledValues={disabledValues}
        returnTimeLowerLimit={returnTimeLowerLimit}
        handleReturnTimeLowerLimit={handleReturnTimeLowerLimit}
        disabledDateForReturnHigherLimit={disabledDateForReturnHigherLimit}
        disabledDateTimeForReturnHigherLimit={
          disabledDateTimeForReturnHigherLimit
        }
        isUberRide={isUberRide}
      />
      {(showOnSiteDriverWait || !isUberRide) && (
        <div className={styles.secondaryRideOptions}>
          {showOnSiteDriverWait && (
            <div className={styles.driverRemainOnSiteRow}>
              <span className={styles.driverRemainOnSiteLabel}>
                Is driver required to remain on-site between Leg A & B
              </span>
              <Radio.Group
                className={styles.driverRemainOnSiteRadioGroup}
                name="driverRemainOnSite"
                value={values?.driverRemainOnSite ?? false}
                onChange={(e) =>
                  setFieldValue("driverRemainOnSite", e.target.value)
                }
              >
                <Radio value={true}>Yes</Radio>
                <Radio value={false}>No</Radio>
              </Radio.Group>
            </div>
          )}
          {!isUberRide && (
            <label className={styles.recurring}>
              <Checkbox
                name="isRecurring"
                onChange={handleReccuringCheckbox}
                checked={values?.isRecurring}
                disabled={
                  disabledValues?.isRecurring ||
                  values?.isPickUpDateFlexible ||
                  values?.isReturnDateFlexible
                }
                whenFieldDisabledShowTooltip={Boolean(disabledValues?.isRecurring)}
              />
              <span className={styles.checkboxLabelText}>Recurring ride</span>
              <ScheduleOutlined
                className={styles.icon}
                disabled={disabledValues?.isRecurring}
              />
            </label>
          )}
        </div>
      )}
      {values?.isRecurring && (
        <RecurringSection
          values={values}
          validateForm={validateForm}
          pickUpDate={pickUpDate}
          startPointOfRecurring={startPointOfRecurring}
          disabledReccuringValues={disabledValues?.recurrenceRule}
          setStartPointOfRecurring={setStartPointOfRecurring}
          setReturnStartPoint={setReturnStartPoint}
          handleChangeTZ={handleChangeTZ}
          returnStartPoint={returnStartPoint}
          handleStartPointTime={handleStartPointTime}
          handleReturnStartPoint={handleReturnStartPoint}
          disabledDatesForStartPointOfRecurring={
            disabledDatesForStartPointOfRecurring
          }
          minimumRoundTrip={minimumRoundTrip}
          disabledDatesForReturnTimeForRoundTripRecurring={
            disabledDatesForReturnTimeForRoundTripRecurring
          }
          disabledDateTime={disabledDateTime}
          minDelayToStartRide={minDelayToStartRide}

        />
      )}
    </div>
  );
};
